import * as React from "react";
import * as userService from "../../../models/services/userService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { useState } from "react";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Alert, Box, Grid } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { TextFieldWithButtonGroup } from "source/components/framework/Form/TextFieldWithButtonGroup";
import { ReportExcelReport } from "source/components/framework/Report/ReportExcelReport";
import { IReportGenerated2 } from "source/models/report/reportGenerated2";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { IUserImportItem, UserImportItem } from "source/models/user/userImportItem";
import { EnumHelper } from "ui/Scripts/source/models/enums";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { DownloadButton } from "source/components/framework/Button/DownloadButton/DownloadButton";
import { AppUploadIcon } from "source/components/framework/Icon/AppUploadIcon";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

interface IImportUsersDialogProps {
    companyId: string;
    onOk: () => void;
    onCancel: () => void;
}

interface IImportUsersDialogState {
    file: File;
    sendInvitations: boolean;
    //List
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    reportData: IReportGenerated2;
}

export const ImportUsersDialog = (props: IImportUsersDialogProps) => {
    const [state, setState] = useState<IImportUsersDialogState>(
        {
            file: null,
            sendInvitations: false,
            //List
            sortColumn: "userCode",
            sortOrderIsAsc: true,
            selectedId: "",
            checkedIds: [],
            reportData: null
        }
    );

    const handleCancel = () => {
        props.onCancel();
    };

    const getSaveData = (companyId: string, checkedIds: string[]): SaveDataObj => {
        const importItems: IUserImportItem[] = [];
        checkedIds.forEach(id => {
            const item = state.reportData.tables[0].dataRows.find(i => i.id === id);
            if (!item) return;
            const importItem = new UserImportItem();
            importItem.userCode = item.cols.find(i => i.field === "userCode")?.valueStr ?? "";
            importItem.firstName = item.cols.find(i => i.field === "firstName")?.valueStr ?? "";
            importItem.lastName = item.cols.find(i => i.field === "lastName")?.valueStr ?? "";
            const endDate = item.cols.find(i => i.field === "endDate");
            importItem.endDate = endDate ? endDate.valueNbr : null;
            importItem.email = item.cols.find(i => i.field === "email")?.valueStr ?? "";
            importItem.userGroupName = item.cols.find(i => i.field === "userGroupName")?.valueStr ?? "";
            importItem.defaultCulture = item.cols.find(i => i.field === "defaultCulture")?.valueStr ?? "";
            const recordUser = item.cols.find(i => i.field === "recordUser");
            importItem.recordUser = recordUser ? recordUser.valueNbr === 1 : false;
            if (importItem.recordUser) {
                importItem.employeeType = EnumHelper.RecordEmployeeTypeFromStr(item.cols.find(i => i.field === "employeeType")?.valueStr);
                importItem.unitName = item.cols.find(i => i.field === "unitName")?.valueStr ?? "";
                importItem.postalAddress = item.cols.find(i => i.field === "postalAddress")?.valueStr ?? "";
                importItem.postalCode = item.cols.find(i => i.field === "postalCode")?.valueStr ?? "";
                importItem.city = item.cols.find(i => i.field === "city")?.valueStr ?? "";
                importItem.phone = item.cols.find(i => i.field === "phone")?.valueStr ?? "";
                importItem.taxNumber = item.cols.find(i => i.field === "taxNumber")?.valueStr ?? "";
                importItem.workShiftName = item.cols.find(i => i.field === "workShiftName")?.valueStr ?? "";
                importItem.comment = item.cols.find(i => i.field === "comment")?.valueStr ?? "";
                importItem.companyName = item.cols.find(i => i.field === "companyName")?.valueStr ?? "";
                const startDate = item.cols.find(i => i.field === "startDate");
                importItem.startDate = startDate ? startDate.valueNbr : null;
                importItem.jobTitle = item.cols.find(i => i.field === "jobTitle")?.valueStr ?? "";
            }
            importItems.push(importItem);
        });
        if (!importItems.length) return;
        const result = new SaveDataObj();
        result.append("companyId", companyId);
        result.append("sendInvitations", state.sendInvitations);
        result.append("importItems", importItems);
        return result;
    };

    const importUsers = async (companyId: string, checkedIds: string[]): Promise<ApiSuccess> => {
        if (checkedIds.length < 1) return;
        const saveData = getSaveData(companyId, checkedIds);
        // Call server
        return await AppUtils.callService(() => userService.importUsers(saveData.obj));
    };

    const handleOk = async () => {
        const result = await importUsers(props.companyId, state.checkedIds);
        if (!result) return null;
        props.onOk();
    };

    const uploadFile = async (files: File[]) => {
        if (!files || files.length < 1) return;
        const file = files[0];
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        setState((old) => ({
            ...old,
            file
        }));
        const saveData = new SaveDataObj();
        saveData.append("companyId", props.companyId);
        saveData.append("attachments", await SaveAttachmentBase64.fromFileAttachments([attachment]));
        // Call server
        const reportData = await AppUtils.callService(() => userService.uploadImportFile(saveData.obj));
        setState((old) => ({
            ...old,
            reportData
        }));
    };

    const downloadTemplate = async () => {
        const fileName = await AppUtils.callService(() => userService.downloadImportTemplate());
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.DocumentParameterDownloaded, fileName));
    };

    const handleSetSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        setState((old) => ({
            ...old,
            sortColumn,
            sortOrderIsAsc
        }));
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState((old) => ({
            ...old,
            selectedId,
            checkedIds
        }));
    };

    return (
        <Dialog
            id="importUsersDialog"
            maxWidth="xl"
            maxHeight="90%"
            title={Translations.ImportUsers}
            body={
                <VerticalContainer
                    before={
                        <>
                            <GridRow>
                                <Grid item lg={6}>
                                    <FormField title={Translations.ExcelFile} margin={0}>
                                        <TextFieldWithButtonGroup
                                            type="text"
                                            name="userCode"
                                            title={Translations.ExcelFile}
                                            disabled={true}
                                            value={state.file?.name ?? ""}
                                            showButtons={true}
                                            buttons={[
                                                { label: Translations.UploadDocument, startIcon: <AppUploadIcon />, onClick: null, fileInput: { onChange: uploadFile, accept: ".xlsx,.xlsm" } }
                                            ]}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item lg={6}>
                                    <FormField title={<span>&nbsp;</span>} margin="0 0 0 -10px" padding="0 0 0 10px">
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <FormCheckBox
                                                title={Translations.SendInvitationEmailToImportedUsers}
                                                checked={state.sendInvitations}
                                                onChange={(sendInvitations: boolean) => {
                                                    setState((old) => ({
                                                        ...old,
                                                        sendInvitations
                                                    }));
                                                }}
                                            />
                                            <DownloadButton sx={{ marginLeft: "auto" }} onClick={downloadTemplate}>{Translations.DownloadFileTemplate}</DownloadButton>
                                        </Box>
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item xs>
                                    <FormField title={Translations.UsersToBeImported}>
                                        {!state.reportData &&
                                            <Alert severity="info">{Translations.ImportUserDialogInstruction}</Alert>
                                        }
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </>
                    }
                    main={
                        <ReportExcelReport
                            margin="0"
                            height="100%"
                            reportData={state.reportData}
                            sortingDisabled={false}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            multiSelect={true}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSortData={handleSetSortData}
                            onSetSelectedIds={handleSetSelectedIds}
                        />
                    }
                />
            }
            buttons={[
                { title: Translations.Cancel, variant: "outlined", onClick: handleCancel },
                { title: Translations.Import, disabled: !state.checkedIds.length, variant: "confirmation", onClick: handleOk },
            ]}
            onClose={handleCancel}
        />
    );
};